/**
 * =============================================================================
 * IDS CRM Web - Notification Log Settings
 * =============================================================================
 *
 * PURPOSE:
 * Admin view of outbound notification attempts (email). Shows whether the
 * email layer is live (SES) or dev-logged, lets an admin send a test email,
 * and lists every attempt with its delivery status.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const STATUS_LABELS = {
  sent: 'Sent',
  dev_logged: 'Logged (not sent)',
  failed: 'Failed'
};

function NotificationLogSettings() {
  const { authFetch, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchLog = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      const response = await authFetch(`/api/v1/admin/notification-log?${params}`);
      if (!response.ok) throw new Error('Failed to load notification log');
      const data = await response.json();
      setEntries(data.data || []);
      setEmailEnabled(Boolean(data.email_enabled));
      setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load notification log');
    } finally {
      setLoading(false);
    }
  }, [authFetch, statusFilter]);

  useEffect(() => {
    fetchLog(1);
  }, [fetchLog]);

  const sendTest = async () => {
    try {
      setTestSending(true);
      setTestResult(null);
      const response = await authFetch('/api/v1/admin/notification-log/test-email', {
        method: 'POST',
        body: JSON.stringify({ to: testTo || undefined })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send test email');
      setTestResult({ type: data.devMode ? 'info' : 'success', text: data.message });
      fetchLog(1);
    } catch (err) {
      setTestResult({ type: 'error', text: err.message });
    } finally {
      setTestSending(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="admin-section notification-log-section">
      <div className="section-header">
        <h2>Notifications</h2>
        <p>Every outbound email attempt is recorded here, whether or not delivery is live.</p>
      </div>

      <div className={`notification-mode-banner ${emailEnabled ? 'live' : 'dev'}`}>
        {emailEnabled
          ? 'Email delivery is LIVE via AWS SES. Attempts below marked "Sent" were accepted by the provider.'
          : 'Email delivery is NOT live (no SES configuration). Notifications are recorded as "Logged (not sent)" and no email leaves the system. Set EMAIL_TYPE=ses, AWS_SES_REGION and SES_FROM_ADDRESS to go live.'}
      </div>

      <div className="notification-test-row">
        <input
          type="email"
          value={testTo}
          onChange={e => setTestTo(e.target.value)}
          placeholder={`Send test to... (default: ${user?.email || 'your address'})`}
        />
        <button className="btn-primary" onClick={sendTest} disabled={testSending}>
          {testSending ? 'Sending...' : 'Send Test Email'}
        </button>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="dev_logged">Logged (not sent)</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {testResult && (
        <div className={`notification-test-result ${testResult.type}`}>{testResult.text}</div>
      )}

      {error && <div className="modal-error">{error}</div>}

      {loading ? (
        <div className="loading-page">Loading...</div>
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <p>No notification attempts recorded yet.</p>
          <p>Use "Send Test Email" above, or trigger any workflow that notifies (task assignment, swap request, SLA alert).</p>
        </div>
      ) : (
        <>
          <table className="notification-log-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.notification_id}>
                  <td>{formatDateTime(entry.created_at)}</td>
                  <td>{entry.recipient}</td>
                  <td title={entry.body_preview || ''}>{entry.subject || '-'}</td>
                  <td>
                    <span className={`notification-status ${entry.status}`}>
                      {STATUS_LABELS[entry.status] || entry.status}
                    </span>
                  </td>
                  <td className="notification-detail">
                    {entry.status === 'failed' ? (entry.error || 'Unknown error')
                      : entry.status === 'sent' ? (entry.provider_message_id || '-')
                      : (entry.related_type || '-')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={pagination.page <= 1}
                onClick={() => fetchLog(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} entries)
              </span>
              <button
                className="pagination-btn"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchLog(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default NotificationLogSettings;
